import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { styled, alpha } from '@mui/material/styles';
import { Logo } from '../shared';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: ReactNode;
}

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}));

const ContentWrapper = styled(Container)(() => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '440px !important',
}));

const LogoSection = styled(RouterLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(4),
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover .logo-icon': {
    transform: 'rotate(-10deg) scale(1.1)',
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
  letterSpacing: '-0.02em',
}));

const FormCard = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 20,
  border: `1px solid ${theme.palette.surface.glassBorder}`,
  padding: theme.spacing(4),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: 16,
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.5rem',
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,

  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

const Subtitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  color: theme.palette.text.secondary,
  fontSize: '0.9375rem',
  '& a': {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'color 0.2s',
    borderBottom: `2px solid ${alpha(theme.palette.success.main, 0.5)}`,
    '&:hover': {
      color: theme.palette.success.main,
      borderBottomColor: theme.palette.success.main,
    },
  },
}));

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  return (
    <PageWrapper>
      <ContentWrapper>
        <LogoSection to="/">
          <Logo size="large" className="logo-icon" />
          <LogoText>Lord of Venues</LogoText>
        </LogoSection>

        <FormCard>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {children}
        </FormCard>
      </ContentWrapper>
    </PageWrapper>
  );
}
